import { useEffect } from 'react'

interface SEOProps {
  title?: string
  description?: string
  image?: string
  url?: string
  type?: 'website' | 'article'
}

const DEFAULT_TITLE = 'Conero Garage Approved - Si Mangia Bene!'
const DEFAULT_DESCRIPTION = 'La guida ai migliori locali delle Marche approvati dal club 4x4 Conero Garage'
const DEFAULT_IMAGE = 'https://approved.conerogarage.it/images/logo-conero-garage.webp'
const SITE_URL = 'https://approved.conerogarage.it'

/**
 * Hook per gestire i meta tag SEO dinamicamente
 * Aggiorna title, description, Open Graph e Twitter cards
 */
export function useSEO({ title, description, image, url, type = 'website' }: SEOProps = {}) {
  useEffect(() => {
    // Titolo
    const fullTitle = title ? `${title} - Conero Garage Approved` : DEFAULT_TITLE
    document.title = fullTitle

    // Helper per aggiornare/creare meta tag
    const setMetaTag = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name'
      let element = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement

      if (!element) {
        element = document.createElement('meta')
        element.setAttribute(attr, name)
        document.head.appendChild(element)
      }

      element.content = content
    }

    // Meta description
    const desc = description || DEFAULT_DESCRIPTION
    setMetaTag('description', desc)

    // Open Graph
    setMetaTag('og:title', fullTitle, true)
    setMetaTag('og:description', desc, true)
    setMetaTag('og:type', type, true)
    setMetaTag('og:url', url || SITE_URL, true)
    setMetaTag('og:image', image || DEFAULT_IMAGE, true)
    setMetaTag('og:site_name', 'Conero Garage Approved', true)
    setMetaTag('og:locale', 'it_IT', true)

    // Twitter Card
    setMetaTag('twitter:card', 'summary_large_image')
    setMetaTag('twitter:title', fullTitle)
    setMetaTag('twitter:description', desc)
    setMetaTag('twitter:image', image || DEFAULT_IMAGE)

    // Cleanup: ripristina i default quando il componente si smonta
    return () => {
      document.title = DEFAULT_TITLE
      setMetaTag('description', DEFAULT_DESCRIPTION)
      setMetaTag('og:title', DEFAULT_TITLE, true)
      setMetaTag('og:description', DEFAULT_DESCRIPTION, true)
      setMetaTag('og:type', 'website', true)
      setMetaTag('og:url', SITE_URL, true)
      setMetaTag('og:image', DEFAULT_IMAGE, true)
    }
  }, [title, description, image, url, type])
}

export default useSEO
